/**
 * The product event stream from the browser's side (IMPLEMENTATION.md R9 b): a queue of screen
 * views and named actions, sent in batches to `POST /v1/events` every few seconds and when the
 * tab goes away. One session id per tab, kept in sessionStorage. Nothing here carries a field
 * value or a document: a screen is a route pattern, a detail is a source or a count.
 */
import type { PaigeClient } from '@paige/api-client';
import { visitorId } from './visitor';

export interface TrackOptions {
  /** The app-relative route pattern, `/t/$tenant/jobs/$job/review`; never a concrete URL. */
  screen?: string;
  feature?: string;
  durationMs?: number;
  detail?: Record<string, string | number | boolean>;
}

interface Pending extends TrackOptions {
  name: string;
  at: string;
}

const SESSION_KEY = 'paige.session';
const FLUSH_EVERY_MS = 10_000;
const MAX_BATCH = 200;

let client: PaigeClient | null = null;
let tenant: string | null = null;
let queue: Pending[] = [];
let timer: ReturnType<typeof setInterval> | null = null;

/** Names are `area.action`, lower case; anything else is a bug and is dropped, never sent. */
const wellFormed = /^[a-z][a-z0-9_]{0,39}\.[a-z][a-z0-9_]{0,39}$/;

export function sessionId(): string {
  try {
    const kept = sessionStorage.getItem(SESSION_KEY);
    if (kept) return kept;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return 'no-storage';
  }
}

/** Which company the next batches are filed under; null before sign-in and on staff screens. */
export function setAnalyticsTenant(slug: string | null): void {
  if (slug !== tenant) void flushAnalytics();
  tenant = slug;
}

/** The company the stream files under now; the error reports (client-errors.ts) go under the same one. */
export function currentAnalyticsTenant(): string | null {
  return tenant;
}

export function track(name: string, options: TrackOptions = {}): void {
  if (!wellFormed.test(name)) return;
  queue.push({ name, at: new Date().toISOString(), ...options });
  if (queue.length >= MAX_BATCH) void flushAnalytics();
}

/** Takes back the newest queued view of a screen that turned out to be a hop; nothing if it already went. */
export function retract(screen: string): void {
  for (let i = queue.length - 1; i >= 0; i -= 1) {
    if (queue[i]?.name === 'screen.viewed' && queue[i]?.screen === screen) {
      queue.splice(i, 1);
      return;
    }
  }
}

/** Sends what is queued; a failure drops the batch rather than retrying, analytics is best effort. */
export async function flushAnalytics(): Promise<void> {
  if (!client || queue.length === 0) return;
  const events = queue;
  queue = [];
  const body = { sessionId: sessionId(), visitorId: visitorId(), events };
  try {
    await client.POST('/events', {
      body,
      keepalive: true,
      ...(tenant ? { params: { header: { 'Paige-Tenant': tenant } } } : {}),
    });
  } catch {
    /* the batch is gone; the next one carries on */
  }
}

/** Starts the stream: the client to post through, a timer, and a flush when the tab hides. */
export function startAnalytics(paige: PaigeClient): () => void {
  client = paige;
  timer = setInterval(() => void flushAnalytics(), FLUSH_EVERY_MS);
  const onHide = () => {
    if (document.visibilityState === 'hidden') void flushAnalytics();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', () => void flushAnalytics());
  return () => {
    if (timer) clearInterval(timer);
    timer = null;
    document.removeEventListener('visibilitychange', onHide);
    client = null;
  };
}

/** For tests: what is waiting to be sent. */
export function pendingEvents(): readonly Pending[] {
  return queue;
}

export function resetAnalyticsForTests(): void {
  queue = [];
  tenant = null;
  client = null;
}
